import { useEffect, useMemo, useState } from "react";
import type {
  CraftingComponent,
  CraftingSocket,
  MaterialDefinition,
} from "@/lib/crafting/types";
import { SOCKET_LABELS } from "@/lib/crafting/types";
import { ComponentTile } from "./ComponentTile";

const COLUMNS = 6;

export interface ComponentGridProps {
  socket: CraftingSocket;
  materials: Record<string, MaterialDefinition>;
  selectedHash?: number;
  equippedHash?: number;
  currentLevel: number;
  onSelect: (plugHash: number) => void;
  onPreview: (component: CraftingComponent | undefined) => void;
}

/**
 * The perk grid for one socket.
 *
 * The game splits normal and enhanced onto two rows, so we do the same: base
 * options on top, their enhanced counterparts below, paginated six at a time.
 * Enhanced options are aligned under the base perk they upgrade wherever the
 * pairing is unambiguous, which is how the game reads.
 */
export function ComponentGrid({
  socket,
  selectedHash,
  equippedHash,
  currentLevel,
  onSelect,
  onPreview,
}: ComponentGridProps) {
  const [page, setPage] = useState(0);

  const { rows, pageCount } = useMemo(() => {
    const base = socket.options.filter((o) => !o.enhanced);
    const enhanced = socket.options.filter((o) => o.enhanced);

    // Pair enhanced options under their base version by name; anything without a
    // partner is appended so nothing is ever hidden.
    const byName = new Map(enhanced.map((e) => [e.name, e] as const));
    const paired: (CraftingComponent | undefined)[] = base.map((b) => byName.get(b.name));
    const unpaired = enhanced.filter((e) => !base.some((b) => b.name === e.name));

    const top: (CraftingComponent | undefined)[] = [...base, ...unpaired.map(() => undefined)];
    const bottom: (CraftingComponent | undefined)[] = [...paired, ...unpaired];

    const count = Math.max(1, Math.ceil(top.length / COLUMNS));
    return { rows: { top, bottom }, pageCount: count };
  }, [socket]);

  // A different socket means a different option list — go back to page one.
  useEffect(() => {
    setPage(0);
  }, [socket.key]);

  const start = page * COLUMNS;
  const slice = (row: (CraftingComponent | undefined)[]) => row.slice(start, start + COLUMNS);
  const hasEnhanced = rows.bottom.some(Boolean);

  return (
    <div
      role="tabpanel"
      id={`panel-${socket.key}`}
      aria-labelledby={`slot-${socket.key}`}
      // Clearing the preview here rather than per-tile is what stops the detail
      // panel flickering as the pointer crosses the gaps between tiles.
      onMouseLeave={() => onPreview(undefined)}
      className="flex items-start gap-2"
    >
      <PageButton
        direction="previous"
        disabled={page === 0}
        onClick={() => setPage((p) => Math.max(0, p - 1))}
      />

      <div className="w-fit">
        <div
          role="group"
          aria-label={`${SOCKET_LABELS[socket.key]} options`}
          className="space-y-0.5"
        >
          <Row
            options={slice(rows.top)}
            selectedHash={selectedHash}
            equippedHash={equippedHash}
            currentLevel={currentLevel}
            onSelect={onSelect}
            onPreview={onPreview}
          />
          {hasEnhanced && (
            <Row
              options={slice(rows.bottom)}
              selectedHash={selectedHash}
              equippedHash={equippedHash}
              currentLevel={currentLevel}
              onSelect={onSelect}
              onPreview={onPreview}
            />
          )}
        </div>

        {pageCount > 1 && (
          <div className="mt-3 flex items-center justify-center gap-2">
            {Array.from({ length: pageCount }, (_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Page ${i + 1} of ${pageCount}`}
                aria-current={i === page ? "true" : undefined}
                onClick={() => setPage(i)}
                className={[
                  "h-1 w-6 transition-colors",
                  i === page ? "bg-line" : "bg-white/20 hover:bg-white/40",
                ].join(" ")}
              />
            ))}
          </div>
        )}
      </div>

      <PageButton
        direction="next"
        disabled={page >= pageCount - 1}
        onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
      />
    </div>
  );
}

function Row({
  options,
  selectedHash,
  equippedHash,
  currentLevel,
  onSelect,
  onPreview,
}: {
  options: (CraftingComponent | undefined)[];
  selectedHash?: number;
  equippedHash?: number;
  currentLevel: number;
  onSelect: (plugHash: number) => void;
  onPreview: (component: CraftingComponent | undefined) => void;
}) {
  return (
    // `w-fit` matters: without it the six columns stretch across the whole
    // container and the tiles sit marooned in ~180px cells, which reads as a
    // huge gap even though `gap` is tiny.
    <div className="grid w-fit grid-cols-6 gap-0.5">
      {Array.from({ length: COLUMNS }, (_, i) => {
        const option = options[i];
        if (!option) return <span key={i} aria-hidden className="h-16 w-16" />;
        return (
          <ComponentTile
            key={option.plugHash}
            component={option}
            selected={option.plugHash === selectedHash}
            equipped={option.plugHash === equippedHash}
            currentLevel={currentLevel}
            onSelect={() => onSelect(option.plugHash)}
            onPreview={() => onPreview(option)}
          />
        );
      })}
    </div>
  );
}

function PageButton({
  direction,
  disabled,
  onClick,
}: {
  direction: "previous" | "next";
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`${direction === "previous" ? "Previous" : "Next"} page of options`}
      className="mt-6 grid h-10 w-7 shrink-0 place-items-center text-ink-2 transition-opacity hover:text-ink disabled:pointer-events-none disabled:opacity-20"
    >
      <span aria-hidden className="text-lg">
        {direction === "previous" ? "◀" : "▶"}
      </span>
    </button>
  );
}
